import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

// Helper to get valid admin access token, refresh if needed
async function getAdminAccessToken() {
  let accessToken = localStorage.getItem('gogig_admin_access');
  const refreshToken = localStorage.getItem('gogig_admin_refresh');
  if (accessToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.status !== 401) return accessToken;
    } catch {}
  }
  if (refreshToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('gogig_admin_access', data.accessToken);
        return data.accessToken;
      }
    } catch {}
  }
  localStorage.removeItem('gogig_admin_access');
  localStorage.removeItem('gogig_admin_refresh');
  window.location.href = '/admin';
  return null;
}

async function adminFetch(url, options = {}) {
  const token = await getAdminAccessToken();
  if (!token) throw new Error('Not authenticated');
  const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
  return fetch(url, { ...options, headers });
}

const ALL_COMPANIES_VALUE = '__all__';

function AdminRetentionReport({ getToken, fetch: fetchProp, retentionReportPath }) {
  const fetchFn = fetchProp || adminFetch;
  const apiPath = retentionReportPath || '/api/documents/retention-report';
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(ALL_COMPANIES_VALUE);
  const [companySearch, setCompanySearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  // Fetch companies when year changes
  useEffect(() => {
    if (!year) {
      setCompanies([]);
      setSelectedCompany(ALL_COMPANIES_VALUE);
      return;
    }
    let cancelled = false;
    setCompaniesLoading(true);
    fetchFn(`${API_URL}${apiPath}?year=${year}&companies=1`)
      .then(res => res.ok ? res.json() : { success: false })
      .then(data => {
        if (cancelled) return;
        setCompaniesLoading(false);
        if (data.success && Array.isArray(data.companies)) {
          setCompanies(data.companies);
        } else {
          setCompanies([]);
        }
        setSelectedCompany(ALL_COMPANIES_VALUE);
      })
      .catch(() => {
        if (!cancelled) setCompaniesLoading(false);
        setCompanies([]);
      });
    return () => { cancelled = true; };
  }, [year, fetchFn, apiPath]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDownload = async () => {
    setError('');
    if (!year) return setError('Select year');
    setLoading(true);
    try {
      let url = `${API_URL}${apiPath}?year=${year}&format=csv`;
      if (selectedCompany && selectedCompany !== ALL_COMPANIES_VALUE) {
        url += `&company=${encodeURIComponent(selectedCompany)}`;
      }
      const res = await fetchFn(url);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      const safeCompany = selectedCompany && selectedCompany !== ALL_COMPANIES_VALUE
        ? selectedCompany.replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 80) : '';
      a.download = safeCompany ? `retention_report_${year}_${safeCompany}.csv` : `retention_report_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlObj);
    } catch (e) {
      setError('Download failed');
    }
    setLoading(false);
  };

  const filteredCompanies = companySearch.trim()
    ? companies.filter(c => c.toLowerCase().includes(companySearch.trim().toLowerCase()))
    : companies;
  const displayLabel = selectedCompany === ALL_COMPANIES_VALUE ? 'All companies' : selectedCompany;

  return (
    <div className="gogig-card" style={{ maxWidth: 480, margin: '0 auto', padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>Retention Report</h2>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Year</label>
        <input
          type="number"
          value={year}
          onChange={e => setYear(e.target.value)}
          style={{ width: 100, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
          min="2020"
          max={new Date().getFullYear()}
        />
      </div>
      <div style={{ marginBottom: 14 }} ref={dropdownRef}>
        <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Company</label>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              background: '#fff',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span style={{ color: selectedCompany ? '#111' : '#6b7280' }}>
              {companiesLoading ? 'Loading companies...' : displayLabel}
            </span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{dropdownOpen ? '▲' : '▼'}</span>
          </button>
          {dropdownOpen && !companiesLoading && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                zIndex: 10,
                maxHeight: 280,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <input
                type="text"
                placeholder="Search company..."
                value={companySearch}
                onChange={e => setCompanySearch(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                style={{
                  padding: '10px 12px',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  outline: 'none',
                  fontSize: 14
                }}
              />
              <div style={{ overflowY: 'auto', maxHeight: 220 }}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCompany(ALL_COMPANIES_VALUE);
                    setDropdownOpen(false);
                    setCompanySearch('');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    background: selectedCompany === ALL_COMPANIES_VALUE ? '#f0fdf4' : 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  All companies
                </button>
                {filteredCompanies.length === 0 ? (
                  <div style={{ padding: 12, color: '#6b7280', fontSize: 14 }}>No company found</div>
                ) : (
                  filteredCompanies.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        setSelectedCompany(c);
                        setDropdownOpen(false);
                        setCompanySearch('');
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: 'none',
                        background: selectedCompany === c ? '#f0fdf4' : 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: 14
                      }}
                    >
                      {c}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
      <button className="gogig-card-btn" onClick={handleDownload} disabled={loading || companiesLoading}>
        {loading ? 'Downloading...' : selectedCompany && selectedCompany !== ALL_COMPANIES_VALUE ? 'Download CSV (company)' : 'Download CSV (all)'}
      </button>
    </div>
  );
}

export default AdminRetentionReport; 